/**
 * Lifecycle of the device database: one SQLite file per signed-in user, opened by exactly one tab,
 * created on first sign-in, deleted on sign-out. Nothing about it is shared between users on a
 * shared phone.
 */
import { getMeta, putMeta } from "./repo";
import { migrate } from "./schema";
import { WorkerSqlDriver } from "./workerDriver";

export interface LocalDatabase {
  userId: string;
  driver: WorkerSqlDriver;
  /** False when persistent storage is unavailable: works this session, gone on reload. */
  persistent: boolean;
  persistenceNote: string | null;
}

export type OpenOutcome =
  | { status: "ready"; db: LocalDatabase }
  | { status: "locked" }
  | { status: "failed"; error: string };

let current: LocalDatabase | null = null;
let opening: { userId: string; promise: Promise<OpenOutcome> } | null = null;
const heldLocks = new Set<string>();

export const currentDatabase = (): LocalDatabase | null => current;

/**
 * SQLite's persistent storage allows one writer, so a second tab (or a second installed-app
 * window) must not open the same file. A Web Lock, held for the life of the page, arbitrates.
 */
function acquireLock(userId: string): Promise<boolean> {
  if (heldLocks.has(userId)) return Promise.resolve(true);
  if (typeof navigator === "undefined" || !("locks" in navigator)) return Promise.resolve(true);
  return new Promise((resolve) => {
    void navigator.locks.request(`ledger-db:${userId}`, { ifAvailable: true }, (lock) => {
      if (!lock) {
        resolve(false);
        return undefined;
      }
      heldLocks.add(userId);
      resolve(true);
      return new Promise<void>(() => undefined); // hold until the page is closed
    });
  });
}

async function openNow(userId: string): Promise<OpenOutcome> {
  if (!(await acquireLock(userId))) return { status: "locked" };
  const driver = new WorkerSqlDriver();
  try {
    const { persistent, reason } = await driver.open(userId);
    await migrate(driver);
    if ((await getMeta(driver, "user_id")) === null) await putMeta(driver, "user_id", userId);
    current = { userId, driver, persistent, persistenceNote: persistent ? null : (reason ?? null) };
    return { status: "ready", db: current };
  } catch (error) {
    driver.terminate();
    return { status: "failed", error: error instanceof Error ? error.message : String(error) };
  }
}

export async function openLocalDatabase(userId: string): Promise<OpenOutcome> {
  if (current?.userId === userId) return { status: "ready", db: current };
  if (opening?.userId === userId) return opening.promise;
  await closeLocalDatabase(); // a different user was open: release it first
  const promise = openNow(userId).finally(() => {
    opening = null;
  });
  opening = { userId, promise };
  return promise;
}

/** Releases the open database but keeps its file (e.g. the session expired; the data is still theirs). */
export async function closeLocalDatabase(): Promise<void> {
  const db = current;
  current = null;
  if (!db) return;
  try {
    await db.driver.close();
  } finally {
    db.driver.terminate();
  }
}

/** Deletes a user's local database entirely: sign-out, or a revoked session. */
export async function destroyLocalDatabase(userId: string): Promise<void> {
  if (current?.userId !== userId) {
    // Not open here (e.g. the session was found revoked on a cold start): open just to unlink it.
    const driver = new WorkerSqlDriver();
    try {
      await driver.open(userId);
      await driver.destroy();
    } finally {
      driver.terminate();
    }
    return;
  }
  const db = current;
  current = null;
  try {
    await db.driver.destroy();
  } finally {
    db.driver.terminate();
  }
}
